import { z } from 'zod';

export const excelImportTypeSchema = z.enum([
  'INITIAL_DATA',
  'HYBRID',
]);

export const excelFileInfoSchema = z.object({
  id: z.number().int().positive(),
  fileUrl: z.string().url(),
  lang: z.string().min(1).max(64),
  createdAt: z.string().datetime(),
  size: z.number().int().nonnegative().optional(),
  user: z.object({
    id: z.number().int().positive(),
    username: z.string().min(1).max(255),
    name: z.string().max(255).nullable(),
  }).strict().nullable(),
  approvalStatus: z.enum(['PENDING', 'APPROVED', 'REJECTED']),
  reviewReason: z.string().max(5_000).nullable().optional(),
  importType: excelImportTypeSchema,
}).strict();

const responseMetaSchema = z.object({
  request_id: z.string().uuid(),
  served_at: z.string().datetime(),
}).strict();

export const initialDataImportsResponseSchema = z.object({
  error: z.literal(false),
  status: z.literal(200),
  message: z.literal('INITIAL_DATA_IMPORTS_READ'),
  data: z.array(excelFileInfoSchema).max(10_000),
  meta: responseMetaSchema,
}).strict();

const phraseMutationDataSchema = z.object({
  fileId: z.number().int().positive(),
  rows: z.number().int().nonnegative(),
}).strict();

const excelUploadDataSchema = z.discriminatedUnion('approvalStatus', [
  z.object({
    excelFileId: z.number().int().positive(),
    approvalStatus: z.literal('PENDING'),
    transcriptionDatasetImported: z.literal(false),
    transcriptionSourcePhraseCount: z.literal(0),
  }).strict(),
  z.object({
    excelFileId: z.number().int().positive(),
    approvalStatus: z.literal('APPROVED'),
    fileUrl: z.string().url(),
    transcriptionDatasetImported: z.boolean(),
    transcriptionSourcePhraseCount: z.number().int().nonnegative(),
  }).strict(),
]);

export const excelUploadResponseSchema = z.object({
  error: z.literal(false),
  status: z.literal(201),
  message: z.literal('EXCEL_IMPORT_UPLOADED'),
  data: excelUploadDataSchema,
  meta: responseMetaSchema,
}).strict();

export const addPhraseResponseSchema = z.object({
  error: z.literal(false),
  status: z.literal(201),
  message: z.literal('EXCEL_PHRASE_ADDED'),
  data: phraseMutationDataSchema,
  meta: responseMetaSchema,
}).strict();

export const removePhrasesResponseSchema = z.object({
  error: z.literal(false),
  status: z.literal(201),
  message: z.literal('EXCEL_PHRASES_REMOVED'),
  data: phraseMutationDataSchema.extend({
    removed: z.number().int().positive().max(200),
  }).strict(),
  meta: responseMetaSchema,
}).strict();

export const importPhrasesResponseSchema = z.object({
  error: z.literal(false),
  status: z.literal(201),
  message: z.literal('EXCEL_PHRASES_IMPORTED'),
  data: phraseMutationDataSchema.extend({
    added: z.number().int().nonnegative().max(5_000),
    skipped: z.number().int().nonnegative().max(5_000),
  }).strict(),
  meta: responseMetaSchema,
}).strict();

export type ExcelFileInfo = z.infer<typeof excelFileInfoSchema>;
